import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { collection, getDocs, onSnapshot, where, or, query, orderBy, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";


// Styles
import "./HomeScreen.css";
import "./SearchButton.css";
import "./TopSparks.css";

// Components
import BottomNav from "./BottomNav";
import CompetitionTimer from "./components/CompetitionTimer";
import SaveButton from "./components/SaveButton";
import ThreeDotsMenu from "./components/ThreeDotsMenu"; // ✅ NEW
import ConfirmModal from "./components/ConfirmModal"; // ✅ NEW
import TopMindsBadge from "./TopMindsBadge";

// Hooks
import useBlockedUsers from "./hooks/useBlockedUsers"; // ✅ NEW

// Mascot
import { useMascot } from "./mascot/MascotContext";
import Mascot from "./mascot/Mascot";
import TutorBubble from "./components/TutorBubble";


// Assets
import InboxImg from "./assets/inbox.png";
import AppLogo from "./assets/Icon.png";
import mascotImg from "./assets/App-logo-final.png";

// Utils
import { getAuthor, authorAvatarUrl } from "./utils/authorLookup";
import { getImageUrl, getThumbUrl } from "./utils/imageUrls";

// ✅ Feed cache: kept in memory while the app is open, so going into an idea and
// pressing back (or switching tabs) does not refetch and reshuffle everything.
const FEED_TTL = 3 * 60 * 1000;
let feedCache = null; // { at, rewarded, all }

// Turns a Firestore idea document into what the cards need.
// Image URLs are public and built instantly (no network request).
const buildIdea = async (d) => {
  const data = d.data();
  const author = await getAuthor(data.uid); // shared, deduplicated, cached
  return {
    id: d.id,
    ...data,
    authorName: (author && author.name) || "Anonymous",
    profileImageUrl: authorAvatarUrl(author),
    authorTopMinds: !!(author && author.topMinds), // Top Minds award (set manually on the user doc)
    imageUrl: getImageUrl(data.imagePath),   // full size, used by the detail pages
    thumbUrl: getThumbUrl(data.imagePath),   // small, used by the cards on this page
  };
};

const HomePage = () => {
  // --- States ---
  const [loading, setLoading] = useState(true);
  const [rewardedIdeas, setRewardedIdeas] = useState([]);
  const [allIdeas, setAllIdeas] = useState([]);
  const [index, setIndex] = useState(0);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [unseenMessageCount, setUnseenMessageCount] = useState(0);
  const [sparkTab, setSparkTab] = useState("top"); // "top" | "funded"

  const navigate = useNavigate();
  // eslint-disable-next-line
  const location = useLocation();
  const trackRef = useRef();
  const auth = getAuth();
  const user = auth.currentUser;

  // ✅ NEW: Block system
  const { blockedIds, blockUser } = useBlockedUsers();

  const [blockTarget, setBlockTarget] = useState(null); // uid waiting for confirmation
  const [blocking, setBlocking] = useState(false);
  const [blockError, setBlockError] = useState("");

  // Called by the three dots menu: just opens the in-app modal
  const handleBlock = (uid) => {
    setBlockError("");
    setBlockTarget(uid);
  };

  const confirmBlock = async () => {
    try {
      setBlocking(true);
      await blockUser(blockTarget);
      setBlockTarget(null);
    } catch (e) {
      console.error("Block failed:", e);
      setBlockError("Couldn't block this user. Please try again.");
    } finally {
      setBlocking(false);
    }
  };

  // Mascot manual trigger — tapping the header logo
  const { showMascot } = useMascot();

  // ✅ Open the full view of a Top Spark
  const openTopSpark = (idea) => navigate(`/top-spark/${idea.id}`, { state: { idea } });

  // ✅ Main Data Fetch (The Heart of the App)
  useEffect(() => {
    let cancelled = false;

    // Fresh copy from earlier in this session? Use it, no reads at all.
    if (feedCache && Date.now() - feedCache.at < FEED_TTL) {
      setRewardedIdeas(feedCache.rewarded);
      setAllIdeas(feedCache.all);
      setLoading(false);
      return undefined;
    }

    const fetchIdeas = async () => {
      try {
        setLoading(true);

        // 1. Fetch Top 10 Top Sparks (ranked manually in PostedCompetitionIdeas)
        const competitionSnap = await getDocs(
          query(collection(db, "PostedCompetitionIdeas"), where("rank", "in", [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]), orderBy("rank", "asc"))
        );
        const competitionIdeas = await Promise.all(competitionSnap.docs.map(buildIdea));

        // 2. Fetch All Public Posts
        const snap = await getDocs(query(collection(db, "postedIdeas"), orderBy("createdAt", "desc"), limit(50)));
        const allIdeasArray = await Promise.all(snap.docs.map(buildIdea));

        // Shuffle all ideas
        for (let i = allIdeasArray.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [allIdeasArray[i], allIdeasArray[j]] = [allIdeasArray[j], allIdeasArray[i]];
        }

        feedCache = { at: Date.now(), rewarded: competitionIdeas, all: allIdeasArray };

        if (!cancelled) {
          setRewardedIdeas(competitionIdeas);
          setAllIdeas(allIdeasArray);
        }
      } catch (error) {
        console.error("Error fetching ideas:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchIdeas();

    return () => {
      cancelled = true;
    };
  }, []);

  // ✅ Auto-scroll Carousel Logic
  useEffect(() => {
    let interval;
    let isInteracting = false;
    const track = trackRef.current;
    if (!track) return;

    const startAutoScroll = () => {
      interval = setInterval(() => {
        if (!isInteracting && rewardedIdeas.length > 1) {
          setIndex((prev) => (prev + 1) % rewardedIdeas.length);
        }
      }, 5000);
    };

    const pause = () => (isInteracting = true);
    const resume = () => (isInteracting = false);

    startAutoScroll();
    track.addEventListener("mouseenter", pause);
    track.addEventListener("mouseleave", resume);
    track.addEventListener("touchstart", pause);
    track.addEventListener("touchend", () => setTimeout(resume, 1000));

    return () => {
      clearInterval(interval);
      track.removeEventListener("mouseenter", pause);
      track.removeEventListener("mouseleave", resume);
      track.removeEventListener("touchstart", pause);
    };
  }, [rewardedIdeas.length]);

  // ✅ Inbox & Messaging Logic
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "messages"), or(where("to", "==", "all"), where("to", "==", user.uid)));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const seenIds = JSON.parse(localStorage.getItem("seenMessageIds") || "[]");
      const unseen = snapshot.docs.filter((msg) => !seenIds.includes(msg.id));
      setHasNewMessages(unseen.length > 0);
      setUnseenMessageCount(unseen.length);
    });
    return () => unsubscribe();
  }, [user]);

  // ✅ Carousel Alignment Fix
  useEffect(() => {
    if (!trackRef.current || rewardedIdeas.length === 0) return;
    const card = trackRef.current.children[index];
    if (!card) return;
    const style = window.getComputedStyle(trackRef.current);
    const gap = parseInt(style.gap) || 16;
    const cardWidth = card.offsetWidth;
    const containerWidth = trackRef.current.parentElement.offsetWidth;
    const totalTrackWidth = trackRef.current.scrollWidth;

    let moveDistance = (cardWidth + gap) * index;
    if (window.innerWidth >= 768) {
      const centerOffset = (containerWidth / 2) - (cardWidth / 2);
      moveDistance = moveDistance - centerOffset;
      const maxMove = totalTrackWidth - containerWidth;
      moveDistance = Math.max(0, Math.min(moveDistance, maxMove));
    }
    trackRef.current.style.transform = `translateX(${-moveDistance}px)`;
  }, [index, rewardedIdeas.length]);

  return (
    <>
      <div className="homepage-container">


        <header className="header-home">
          <div className="trescend-header-inner">
            {/* Row 1: logo + Trescend name (left) | inbox (right) */}
            <div className="header-top-row">
              <div className="header-left">
                <img
                  src={mascotImg}
                  alt="Trescend mascot"
                  className="header-mascot-img"
                  onClick={() => showMascot("corner")}
                  style={{ cursor: "pointer" }}
                />
                <span className="header-brand-name">Trescend</span>
              </div>

              <div className="header-actions">
                <span className="header-icon-slot" onClick={() => navigate("/inbox")}>
                  <img src={InboxImg} alt="Inbox" className="header-icon" />
                  {(unseenMessageCount > 0 || hasNewMessages) && <span className="notification-dot" />}
                </span>
              </div>
            </div>

            {/* Row 2: search bar */}
            <div className="search-row-container">
              <div className="search-button-wrapper-home" onClick={() => navigate("/search-people")}>
                <img src={require("./assets/LENS.png")} alt="Search" className="search-button-icon" />
                <span className="search-button-text-home">Search for Journeys</span>
              </div>
            </div>
          </div>
        </header>

        <CompetitionTimer loading={loading} />


        <section className="section rewarded-ideas-section ts-root">
          {/* Tabs: Top Sparks | Funded Ideas */}
          <div className="ts-tabs" role="tablist" aria-label="Sparks sections">
            <button
              type="button"
              role="tab"
              aria-selected={sparkTab === "top"}
              className={`ts-tab ${sparkTab === "top" ? "active" : ""}`}
              onClick={() => setSparkTab("top")}
            >
              Top Sparks
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={sparkTab === "funded"}
              className={`ts-tab ${sparkTab === "funded" ? "active" : ""}`}
              onClick={() => setSparkTab("funded")}
            >
              Funded Ideas
              <span className="ts-tab-soon">Soon</span>
            </button>
          </div>

          {/* Top Sparks (kept mounted so the carousel keeps working) */}
          <div
            className={`ts-pane ${sparkTab === "top" ? "" : "ts-pane-hidden"}`}
            aria-hidden={sparkTab !== "top"}
          >
            <div className={`rewarded-carousel ${loading ? "loading" : ""}`}>
              <div className="carousel-track" ref={trackRef}>
                {loading ? (
                  [1, 2, 3].map((_, i) => (
                    <div key={`skeleton-${i}`} className="ts-card ts-skeleton" aria-hidden="true">
                      <div className="ts-sk ts-sk-line short"></div>
                      <div className="ts-sk ts-sk-photo"></div>
                      <div className="ts-sk ts-sk-line"></div>
                      <div className="ts-sk ts-sk-line mid"></div>
                      <div className="ts-sk ts-sk-line short"></div>
                    </div>
                  ))
                ) : (
                  rewardedIdeas.map((idea, i) => (
                    <article
                      key={idea.id || i}
                      className="ts-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => openTopSpark(idea)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openTopSpark(idea);
                        }
                      }}
                    >
                      <div className="ts-dateline">
                        <span>Top Spark</span>
                        <span>No. {idea.rank}</span>
                      </div>

                      <figure className="ts-photo">
                        {idea.thumbUrl || idea.imageUrl ? (
                          <img src={idea.thumbUrl || idea.imageUrl} alt={idea.title || "Idea"} className="ts-photo-img" loading="lazy" />
                        ) : (
                          <div className="ts-photo-empty">No photo filed</div>
                        )}

                      </figure>

                      <h3 className="ts-headline">{idea.title || "Untitled Spark"}</h3>

                      <div className="ts-byline">
                        {idea.profileImageUrl ? (
                          <img src={idea.profileImageUrl} alt="" className="ts-avatar" />
                        ) : (
                          <span className="ts-avatar">{idea.authorName?.[0]}</span>
                        )}
                        <span className="ts-author">By <b>{idea.authorName}</b></span>
                      </div>

                      {idea.tags?.length > 0 && (
                        <div className="ts-tags">
                          {idea.tags.slice(0, 3).map((t, idx) => (
                            <span className="ts-tag" key={idx}>{t}</span>
                          ))}
                          {idea.tags.length > 3 && <span className="ts-tag">+{idea.tags.length - 3}</span>}
                        </div>
                      )}

                      <div className="ts-readmore">Read the full story</div>
                    </article>
                  ))
                )}
              </div>

              {!loading && rewardedIdeas.length > 1 && (
                <div className="carousel-controls">
                  <button className="arrow shimmer-arrow" onClick={() => setIndex((prev) => (prev - 1 + rewardedIdeas.length) % rewardedIdeas.length)}><FaChevronLeft /></button>
                  <div className="carousel-dots">{rewardedIdeas.map((_, dotIndex) => (<span key={dotIndex} className={`dot ${index === dotIndex ? "active" : ""}`} onClick={() => setIndex(dotIndex)} />))}</div>
                  <button className="arrow shimmer-arrow" onClick={() => setIndex((prev) => (prev + 1) % rewardedIdeas.length)}><FaChevronRight /></button>
                </div>
              )}
            </div>
          </div>

          {/* Funded Ideas: coming soon notice */}
          {sparkTab === "funded" && (
            <div className="ts-funded" role="tabpanel">
              <span className="ts-funded-stamp">Stop press</span>
              <h3 className="ts-funded-headline">Funding is coming to ideas like yours</h3>
              <p className="ts-funded-sub">Win a competition. Get your idea funded.</p>

              <div className="ts-funded-cols">
                <p className="ts-funded-copy">
                  Soon, the best sparks will earn more than applause. Funded Ideas will back the
                  winners of our competitions with real funding, so a great idea can become a real thing.
                  The presses are warming up. Keep sparking, and be ready when this edition lands.
                </p>

              </div>

              <div className="ts-funded-press">
                <span className="ts-press-dot" aria-hidden="true"></span>
                Funded Ideas edition is going to print
              </div>
            </div>
          )}
        </section>


        <section className="section">
          <h2 className="section-title-home">All Ideas</h2>
          <div className="idea-list">
            {allIdeas
              .filter((idea) => !blockedIds.includes(idea.uid)) /* ✅ NEW: hide blocked users */
              .map((idea, i) => (
                <article className="idea-card" key={idea.id || i} onClick={() => navigate(`/view-idea/${idea.id}`, { state: { idea } })}>
                  <header className="card-top" style={idea.authorTopMinds ? { flexWrap: 'wrap' } : undefined}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); navigate(`/journey/${idea.uid}`); }}>
                      {idea.profileImageUrl ? <img src={idea.profileImageUrl} alt={idea.authorName} className="profile-image" /> : <span className="avatar">{idea.authorName?.[0]}</span>}
                     <span className="user-name-home">{idea.authorName}</span>
                    </div>

                    {/* ✅ NEW: three dots menu (Report / Block) */}
                    <ThreeDotsMenu
                      type="idea"
                      id={idea.id}
                      ownerId={idea.uid}
                      currentUid={user?.uid}
                      onBlock={handleBlock}
                    />

                    {idea.authorTopMinds && (
                        <div style={{ flex: '0 0 100%', marginTop: '6px' }}>
                            <TopMindsBadge />
                        </div>
                    )}
                  </header>
                  <div className="card-content">
                    {(idea.thumbUrl || idea.imageUrl) && <img src={idea.thumbUrl || idea.imageUrl} alt={idea.description} className="idea-image" loading="lazy" />}
                    <div className="card-body">
                      {idea.title && <h3 className="idea-title">{idea.title}</h3>}
                      <div className="tags-home">{idea.tags?.map((t, idx) => (<span className="tag" key={idx}>{t}</span>))}</div>
                    </div>
                  </div>
                  {idea.description && <p className="description description-truncated">{idea.description}</p>}
                </article>
              ))}
          </div>
        </section>
      </div>

      <ConfirmModal
        open={!!blockTarget}
        title="Block this user?"
        message="You won't see their ideas anymore. You can't undo this from here yet."
        confirmText="Block"
        danger
        loading={blocking}
        error={blockError}
        onConfirm={confirmBlock}
        onCancel={() => setBlockTarget(null)}
      />

      <Mascot />
      <TutorBubble />
      <BottomNav />
    </>
  );
};

export default HomePage;